import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, Users, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  listBlasts, createBlast, listClients, listListings,
  fairHousingCheck,
} from './crmApi';

const BLAST_TYPES = ['Open House', 'New Listing Alert', 'Property Match'];

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function StatusBadge({ status }) {
  const tone = status === 'sent' ? 'bg-brand-green text-brand-cream'
    : status === 'scheduled' ? 'bg-brand-gold/20 text-brand-gold'
      : 'bg-yellow-100 text-yellow-800';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${tone}`}>
      {status}
    </span>
  );
}

function BlastCard({ blast }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm" data-testid={`blast-row-${blast.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wide text-brand-green/60">{blast.blast_type}</div>
          <div className="font-semibold text-brand-green">{blast.address || 'No address'}</div>
        </div>
        <StatusBadge status={blast.status} />
      </div>
      {blast.open_house?.date && (
        <div className="text-xs text-brand-green/70 mt-1">
          {blast.open_house.date} · {blast.open_house.start} – {blast.open_house.end}
        </div>
      )}
      <p className="text-sm text-brand-green/80 mt-2 whitespace-pre-wrap line-clamp-3">
        {blast.message}
      </p>
      <div className="mt-3 flex flex-wrap gap-3 text-xs text-brand-green/70">
        <span className="inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> {blast.recipient_ids?.length || 0} recipients
        </span>
        {blast.skipped_names?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-yellow-700">
            <AlertTriangle className="w-3 h-3" /> {blast.skipped_names.length} skipped (no opt-in)
          </span>
        )}
        {blast.rsvps?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-brand-green">
            <CheckCircle2 className="w-3 h-3" /> {blast.rsvps.filter((r) => r.status === 'attending').length} attending
          </span>
        )}
        {blast.scheduled_at && (
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Sends {fmt(blast.scheduled_at)}</span>
        )}
        <span className="text-brand-green/50">· Created {fmt(blast.created_at)}</span>
      </div>
    </div>
  );
}

function NewBlastDialog({ open, onClose, onCreated }) {
  const [type, setType] = useState('Open House');
  const [listingId, setListingId] = useState('');
  const [listings, setListings] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedIds, setSelectedIds] = useState({});
  const [message, setMessage] = useState('');
  const [ohDate, setOhDate] = useState('');
  const [ohStart, setOhStart] = useState('');
  const [ohEnd, setOhEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [fhFlags, setFhFlags] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [l, c] = await Promise.all([listListings(), listClients()]);
        setListings(l);
        setClients(c);
      } catch { /* ignore */ }
    })();
  }, [open]);

  // Debounced Fair-Housing pre-check
  useEffect(() => {
    if (!message || message.length < 10) { setFhFlags([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fairHousingCheck(message);
        setFhFlags(res.flags || []);
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
  }, [message]);

  const optedIn = useMemo(() => clients.filter((c) => c.twilio_opt_in_status === 'GRANTED'), [clients]);
  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  const submit = async () => {
    const ids = Object.keys(selectedIds).filter((k) => selectedIds[k]);
    if (!ids.length) return toast({ title: 'Pick at least one recipient', variant: 'destructive' });
    if (fhFlags.length) return toast({ title: 'Fix Fair-Housing flags before sending', variant: 'destructive' });
    setBusy(true);
    try {
      const body = {
        listing_id: listingId || null,
        client_ids: ids,
        message,
        blast_type: type,
        open_house: type === 'Open House' ? { date: ohDate, start: ohStart, end: ohEnd } : null,
      };
      const res = await createBlast(body);
      const delivered = Number(res?.delivered_count || 0);
      const total = (res?.recipients || []).length;
      const status = res?.status;
      if (status === 'sent') {
        toast({ title: `Blast sent to ${delivered}/${total}` });
      } else if (status === 'failed') {
        toast({ title: 'Blast created but sending failed', description: res?.send_error || 'Check Twilio config', variant: 'destructive' });
      } else if (status === 'scheduled') {
        toast({ title: 'Blast scheduled' });
      } else {
        toast({ title: 'Blast queued' });
      }
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const desc = typeof detail === 'string' ? detail : detail?.message || 'Try again.';
      toast({ title: 'Could not create blast', description: desc, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="blast-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New blast</DialogTitle>
          <DialogDescription>
            Only clients with GRANTED SMS opt-in receive the message (TCPA). Fair-Housing terms are checked live.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Blast type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="blast-input-type"><SelectValue /></SelectTrigger>
                <SelectContent>{BLAST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Listing (optional)</Label>
              <Select value={listingId || 'none'} onValueChange={(v) => setListingId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="blast-input-listing"><SelectValue placeholder="No listing" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No listing</SelectItem>
                  {listings.map((l) => <SelectItem key={l.id} value={l.id}>{l.address}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {type === 'Open House' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Date</Label><Input placeholder="03-20-2026" value={ohDate} onChange={(e) => setOhDate(e.target.value)} data-testid="blast-input-ohdate" /></div>
              <div><Label>Start</Label><Input placeholder="1:00 PM" value={ohStart} onChange={(e) => setOhStart(e.target.value)} data-testid="blast-input-ohstart" /></div>
              <div><Label>End</Label><Input placeholder="3:00 PM" value={ohEnd} onChange={(e) => setOhEnd(e.target.value)} data-testid="blast-input-ohend" /></div>
            </div>
          )}

          <div>
            <Label>Message</Label>
            <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
                      placeholder="You're invited! Open house Sat 1-3 PM at 1420 Maple…"
                      data-testid="blast-input-message" />
            {fhFlags.length > 0 && (
              <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2"
                   data-testid="blast-fh-flags">
                Fair-Housing concern: {fhFlags.join(', ')}
              </div>
            )}
          </div>

          <div>
            <Label>Recipients ({selectedCount} selected · {optedIn.length} opted-in clients)</Label>
            <div className="max-h-56 overflow-y-auto border border-brand-cream rounded-lg divide-y divide-brand-cream mt-1">
              {optedIn.length === 0 && (
                <p className="p-3 text-sm text-brand-green/60">
                  No clients with SMS opt-in yet. Add one from the Clients tab.
                </p>
              )}
              {optedIn.map((c) => (
                <label key={c.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-brand-cream/50"
                       data-testid={`blast-recipient-${c.id}`}>
                  <Checkbox checked={!!selectedIds[c.id]}
                            onCheckedChange={(v) => setSelectedIds({ ...selectedIds, [c.id]: !!v })} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-brand-green truncate">{c.name}</div>
                    <div className="text-xs text-brand-green/60">{c.phone_number || c.email || '—'}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !message || !selectedCount || fhFlags.length > 0}
                  data-testid="blast-save-btn">
            {busy ? 'Sending…' : 'Send blast'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CrmBlasts() {
  const [blasts, setBlasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setBlasts(await listBlasts()); }
    catch (err) { toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { refresh(); }, [refresh]);

  const onCreated = () => { setShowForm(false); refresh(); };

  return (
    <div data-testid="crm-blasts">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-brand-green">Blasts</h2>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button onClick={() => setShowForm(true)} data-testid="blast-new-btn">
              <Send className="w-4 h-4 mr-1" /> New blast
            </Button>
          </DialogTrigger>
        </Dialog>
        <NewBlastDialog open={showForm} onClose={() => setShowForm(false)} onCreated={onCreated} />
      </div>

      {loading ? <p className="text-brand-green/60">Loading blasts…</p>
        : blasts.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <p className="text-brand-green/70">No blasts yet.</p>
            <Button className="mt-3" onClick={() => setShowForm(true)}>Send your first blast</Button>
          </div>
        ) : (
          <ul className="space-y-3">
            {blasts.map((b) => <li key={b.id}><BlastCard blast={b} /></li>)}
          </ul>
        )}
    </div>
  );
}
